import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ConsultationPopupProps {
  isOpen: boolean;
  onClose: () => void;
  treatmentTitle: string;
}

export default function ConsultationPopup({ isOpen, onClose, treatmentTitle }: ConsultationPopupProps) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [privacyError, setPrivacyError] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!privacyChecked) {
      setPrivacyError(true);
      return;
    }
    setPrivacyError(false);
    setIsSubmitting(true);
    setIsError(false);

    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d6qaujcddmmni7ck8f8g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setIsSuccess(true);
        form.reset();
        setCharCount(0);
        setPrivacyChecked(false);
      } else {
        setIsError(true);
      }
    } catch {
      setIsError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsSuccess(false);
    setIsError(false);
    setCharCount(0);
    setPrivacyChecked(false);
    setPrivacyError(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{t('popup_consultation_title')}</h2>
            <p className="text-sm text-teal-600 font-medium mt-0.5">{treatmentTitle}</p>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-500 text-lg"></i>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {isSuccess ? (
            <div className="text-center py-8">
              <div className="w-14 h-14 flex items-center justify-center bg-teal-50 rounded-full mx-auto mb-4">
                <i className="ri-check-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{t('popup_success_title')}</h3>
              <p className="text-sm text-gray-500 leading-relaxed mb-6">{t('popup_success_msg')}</p>
              <button
                onClick={handleClose}
                className="px-6 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-md hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t('popup_close')}
              </button>
            </div>
          ) : (
            <form
              id="treatment-consultation-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              {/* Hidden treatment field */}
              <input type="hidden" name="treatment" value={treatmentTitle} />

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {t('popup_name')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="full_name"
                  required
                  placeholder={t('popup_name_placeholder')}
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {t('popup_phone')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  placeholder={t('popup_phone_placeholder')}
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {t('popup_email')}
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder={t('popup_email_placeholder')}
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition"
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {t('popup_message')}
                </label>
                <textarea
                  name="message"
                  rows={3}
                  maxLength={500}
                  placeholder={t('popup_message_placeholder')}
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition resize-none"
                />
                <p className="text-xs text-gray-400 text-right mt-1">{charCount}/500</p>
              </div>

              {/* Privacy Consent */}
              <div className={`flex items-start gap-2.5 p-3 rounded-md border ${privacyError ? 'border-red-300 bg-red-50' : 'border-gray-100 bg-gray-50'}`}>
                <input
                  type="checkbox"
                  id="popup-privacy-consent"
                  checked={privacyChecked}
                  onChange={(e) => {
                    setPrivacyChecked(e.target.checked);
                    if (e.target.checked) setPrivacyError(false);
                  }}
                  className="mt-0.5 w-4 h-4 accent-teal-600 cursor-pointer flex-shrink-0"
                />
                <label htmlFor="popup-privacy-consent" className="text-xs text-gray-600 leading-relaxed cursor-pointer">
                  {t('privacy_consent')}{' '}
                  <span className="text-red-500">*</span>
                </label>
              </div>
              {privacyError && (
                <p className="text-xs text-red-500 -mt-2">{t('privacy_consent_required')}</p>
              )}

              {isError && (
                <p className="text-sm text-red-500">{t('popup_error_msg')}</p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 bg-teal-600 text-white text-sm font-medium rounded-md hover:bg-teal-700 transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? t('popup_submitting') : t('popup_submit')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}